// ================= IMPORTS =================
import { useEffect, useRef } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import {
  adminHasPermission,
  adminHasAnyPermission,
  adminHasAllPermissions,
} from "./utils/adminPermissions";
import {
  parentHasPermission,
  parentHasAnyPermission,
  parentHasAllPermissions,
} from "./utils/parentPermissions";
import {
  studentHasPermission,
  studentHasAnyPermission,
  studentHasAllPermissions,
} from "./utils/studentPermissions";
import {
  tutorHasPermission,
  tutorHasAnyPermission,
  tutorHasAllPermissions,
} from "./utils/tutorPermissions";
import {
  testerHasPermission,
  testerHasAnyPermission,
  testerHasAllPermissions,
} from "./utils/testerPermissions";
import { Box, Typography, CircularProgress } from "@mui/material";
import { masterFormRoutePermissions } from "./constants/masterFormPermissions";

// Pages
import AdminLogin from "./pages/Adminlogin";
import TutorLogin from "./pages/TutorLogin";
import Course from "./pages/Course/Course";
import CourseView from "./pages/Course/CourseView";
import CourseAssignment from "./pages/Course/CourseAssignment";
import CourseAssignmentList from "./pages/Course/CourseAssignmentList";
import CourseContent from "./pages/Course/CourseContent";
import CourseList from "./pages/Course/CourseList";
import CourseReport from "./pages/Course/CourseReport";
import CourseTest from "./pages/Course/CourseTest";
import TestList from "./pages/Course/TestList";
import TopicSubtopicList from "./pages/Course/TopicList";
import TimeSlots from "./pages/Course/CourseTimeSlot";

import AdminLayout from "./pages/Layout/AdminLayout";
import TutorLayout from "./pages/Layout/TutorLayout";
import Layout from "./pages/Layout/Layout";
import ParentLayout from "./pages/Layout/ParentLayout";
import StudentLayout from "./pages/Layout/StudentLayout";

import Login from "./pages/Login";
import ParentSso from "./pages/ParentSso.jsx";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SignUp from "./pages/SignUp";
import Dashboard from "./pages/Student/Dashboard";
import AdminDashboard from "./pages/Dashboard/AdminDashboard";
import ResourceAllocation from "./pages/Course/ResourceAllocation";
import HolidayManagement from "./pages/Course/HolidayManagement";
import AnalyticsLayout from "./pages/Analytics/AnalyticsLayout";
import BatchCapacityAnalytics from "./pages/Analytics/BatchCapacityAnalytics";
import PaymentOverdueAnalytics from "./pages/Analytics/PaymentOverdueAnalytics";
import AcademicSupportAnalytics from "./pages/Analytics/AcademicSupportAnalytics";
import TargetSchoolsAnalytics from "./pages/Analytics/TargetSchoolsAnalytics";
import RevisionPlanningAnalytics from "./pages/Analytics/RevisionPlanningAnalytics";
import GeographicAnalytics from "./pages/Analytics/GeographicAnalytics";
import AppUsageAnalytics from "./pages/Analytics/AppUsageAnalytics";
import NotificationsAnalytics from "./pages/Analytics/NotificationsAnalytics";
import StudentDashboard from "./pages/Student/Dashboard";
import StudentAnnouncements from "./pages/Student/Announcements";
import StudentCertificates from "./pages/Student/Certificates";
import StudentClasses from "./pages/Student/Classes";
import StudentTest from "./pages/Student/StudentTest";
import MyCourseTest from "./pages/Student/Homework/MyCourseTest";
import MyCourseVideo from "./pages/Student/Video/MyCourseVideo";
import MyCourseView from "./pages/Student/Video/MyCourseView";
import MyCourseVideoPage from "./pages/MyCourseVideoPage";
import VideoLessonsPage from "./pages/VideoLessonsPage";

import SubTopicContentView from "./pages/Student/SubTopicContentView";
import SubTopicTest from "./pages/Student/SubTopicTest";
import TopicTest from "./pages/Student/TopicTest";
import TopicContentView from "./pages/Student/TopicContentView";
import Games from "./pages/Student/Games";
import GameLeaderboard from "./pages/Student/GameLeaderboard";
import FlashCards from "./pages/Student/FlashCards";
import StudentProfile from "./pages/Student/StudentProfile";

import AiAnswerSheetUpload from "./pages/Student/AiAnswerSheetUpload";
import WorkSheetTake from "./pages/Student/WorkSheetTake";
import WorkSheetReview from "./pages/Student/WorkSheetReview.jsx";
import WorkSheetSweepHistory from "./pages/Student/WorkSheetSweepHistory.jsx";
import StudentPannel from "./pages/StudentPannel";

// Mock Exam
import MockExamCategory from "./pages/MockExam/MockExamCategory";
import MockExam from "./pages/MockExam/MockExam";
import PaperExtract from "./pages/MockExam/PaperExtract";

// Paper
import Paper from "./pages/Paper/Paper";
import PaperCategory from "./pages/Paper/PaperCategory";

// Master Form
import MasterForm from "./pages/MasterForm/MasterForm";

// Admin
import FlashCardList from "./pages/Admin/FlashCards/FlashCardList";
import FlashCardForm from "./pages/Admin/FlashCards/FlashCardForm";
import GameTopicList from "./pages/Admin/Games/GameTopicList";
import GameTopicForm from "./pages/Admin/Games/GameTopicForm";
import GameTopicView from "./pages/Admin/Games/GameTopicView";
import GameContentList from "./pages/Admin/Games/GameContentList";
import GameContentForm from "./pages/Admin/Games/GameContentForm";
import GameContentView from "./pages/Admin/Games/GameContentView";
import ParentList from "./pages/Admin/ParentList";
import ParentBilling from "./pages/Admin/ParentBilling";
import TutorList from "./pages/Admin/TutorList";
import TutorForm from "./pages/Admin/TutorForm";
import AdminPaperPurchases from "./pages/Admin/PaperPurchases";
import AdminBillingTransactions from "./pages/Admin/AdminBillingTransactions";
import Announcements from "./pages/Admin/Announcements";
import Awards from "./pages/Admin/Awards";
import Certificates from "./pages/Admin/Certificates";
import ExamModeManagement from "./pages/Admin/ExamModeManagement";
import AdminSettings from "./pages/Admin/AdminSettings";

// Admin Student
import StudentList from "./pages/Admin/Student/StudentList";
import AuditTrailList from "./pages/Admin/AuditTrail/AuditTrailList";
import StudentForm from "./pages/Admin/Student/StudentForm";
import StudentDetails from "./pages/Admin/Student/StudentDetails";

// Parent
import Billing from "./pages/Parent/Billing/Billing";
import BillingInformation from "./pages/Parent/BillingInformation";
import AddStudent from "./pages/Parent/Student/AddStudent";
import EditStudent from "./pages/Parent/Student/EditStudent";
import ChangePassword from "./pages/Parent/Student/ChangePassword";
import MyStudentList from "./pages/Parent/Student/MyStudentList";
import ParentDashboard from "./pages/Parent/Dashboard";
import SchoolReadinessDetail from "./pages/Parent/SchoolReadinessDetail";
import ParentReadinessOverview from "./pages/Parent/ParentReadinessOverview";
import ParentAnnouncements from "./pages/Parent/Announcements";
import ParentCertificates from "./pages/Parent/Certificates";
import EndOfReport from "./pages/Parent/Progress/EndOfReport";
import FinishedTest from "./pages/Parent/Progress/FinishedTest";
import Setting from "./pages/Parent/Setting";
import CourseTargetArea from "./pages/Parent/Progress/CourseTargetArea";
import TestScores from "./pages/Parent/Progress/TestScores";
import ParentMockExamResults from "./pages/Parent/Progress/ParentMockExamResults.jsx";
import ParentPastPaperResults from "./pages/Parent/Progress/ParentPastPaperResults.jsx";

import MyCurrentCourseAssignment from "./pages/Student/MyCurrentCourseAssignment";

// Tutor
import TutorDashboard from "./pages/Tutor/TutorDashboard";
import TutorStudents from "./pages/Tutor/TutorStudents";
import TutorCourses from "./pages/Tutor/TutorCourses";
import TutorTimeslots from "./pages/Tutor/TutorTimeslots";
import TutorAssignments from "./pages/Tutor/TutorAssignments";
import TutorTests from "./pages/Tutor/TutorTests";
import TutorMockExams from "./pages/Tutor/TutorMockExams";
import TutorPapers from "./pages/Tutor/TutorPapers";
import TutorAnnouncements from "./pages/Tutor/TutorAnnouncements";
import TutorNotifications from "./pages/Tutor/TutorNotifications";
import TutorAwards from "./pages/Tutor/TutorAwards";
import TutorCertificates from "./pages/Tutor/TutorCertificates";
import TutorStartClass from "./pages/Tutor/TutorStartClass";
import TutorClasses from "./pages/Tutor/TutorClasses";
import TutorMeetingPage from "./pages/Tutor/TutorMeetingPage";
import AdminLeads from "./pages/Admin/AdminLeads";
import WhatsAppCampaign from "./pages/Admin/WhatsAppCampaign.jsx";
import SupportChatInbox from "./pages/Admin/SupportChatInbox.jsx";
import EmailSubscriptions from "./pages/Admin/EmailSubscriptions.jsx";
import TuitionRequests from "./pages/Admin/TuitionRequests";
import MobileRevisionSupportRequests from "./pages/Admin/MobileRevisionSupportRequests";
import MobileHelpSupport from "./pages/Admin/MobileHelpSupport";
import MobileAppIssues from "./pages/Admin/MobileAppIssues";
import MobileUsers from "./pages/Admin/MobileUsers";
import MobileUserDetails from "./pages/Admin/MobileUserDetails";

// 404
import NotFound from "./pages/NotFound";

// AI Assignment Creator
import AiAssignmentCreator from "./pages/Admin/AiAssignmentCreator";

// Question Bank
import QuestionBankList from "./pages/QuestionBank/QuestionBankList";
import QuestionBankForm from "./pages/QuestionBank/QuestionBankForm";
import QuestionBankImport from "./pages/QuestionBank/QuestionBankImport";
import QuestionBankImportReview from "./pages/QuestionBank/QuestionBankImportReview";
import QuestionBankPapers from "./pages/QuestionBank/QuestionBankPapers";
import QuestionBankPaperViewer from "./pages/QuestionBank/QuestionBankPaperViewer";
import QuestionBankRecentImports from "./pages/QuestionBank/QuestionBankRecentImports";
import FlaggedQuestions from "./pages/QuestionBank/FlaggedQuestions";
import SimilarPaperReview from "./pages/QuestionBank/SimilarPaperReview";
import McqReview from "./pages/QuestionBank/McqReview";
import VerificationDashboard from "./pages/Admin/VerificationDashboard";

// Student Mock Exams
import StudentMockExams from "./pages/Student/MockExams/StudentMockExams";
import StudentMockExamTake from "./pages/Student/MockExams/StudentMockExamTake";
// eslint-disable-next-line no-unused-vars -- used in Route elements below
import StudentMockExamReport from "./pages/Student/MockExams/StudentMockExamReport";
// eslint-disable-next-line no-unused-vars -- used in Route elements below
import StudentMockExamExamReport from "./pages/Student/MockExams/StudentMockExamExamReport";
// eslint-disable-next-line no-unused-vars -- used in Route elements below
import StudentMockExamReview from "./pages/Student/MockExams/StudentMockExamReview";

import StudentPastPapers from "./pages/Student/PastPapers/StudentPastPapers";
import StudentPaperTake from "./pages/Student/PastPapers/StudentPaperTake";
import StudentPaperResult from "./pages/Student/PastPapers/StudentPaperResult";
import StudentPaperReview from "./pages/Student/PastPapers/StudentPaperReview";
import StudentPaperReport from "./pages/Student/PastPapers/StudentPaperReport";

// ============ AUTH GUARDS ============

/** API returns e.g. "Super Admin"; login stores `role.toLowerCase()` → "super admin". */
function normalizePortalRole(role) {
  if (role == null) return "";
  return String(role).toLowerCase().trim().replace(/\s+/g, " ");
}

/** Same admin SPA + Passport scope: Admin or Super Admin. */
function isAdminPortalRole(role) {
  const r = normalizePortalRole(role);
  return r === "admin" || r === "super admin" || r === "super_admin";
}

// Redirect via window.location to avoid "The operation is insecure" from history.push
const SafeRedirect = ({ to }) => {
  const location = useLocation();
  useEffect(() => {
    const targetPath = to.startsWith("/") ? to : `/${to}`;
    const currentPath = location.pathname.replace(/\/$/, "") || "/";
    const normalizedTarget = targetPath.replace(/\/$/, "") || "/";
    if (currentPath !== normalizedTarget) {
      window.location.replace(to);
    }
  }, [to, location.pathname]);
  return null;
};

// General Protected Route
const RequireAuth = ({ children }) => {
  const token = localStorage.getItem("token");
  return token ? children : <SafeRedirect to="/login" />;
};

// Admin Protected Route
const RequireAdmin = ({ children }) => {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  return token && (isAdminPortalRole(role) || role === "tester")
    ? children
    : <SafeRedirect to="/login" />;
};

/**
 * Block admin routes unless localStorage admin-permissions matches.
 * Use one of: `permission` (single), `anyOf` (needs one), `allOf` (needs all).
 */
const RequireAdminPermission = ({ permission, anyOf, allOf, children }) => {
  const isTester = localStorage.getItem("role") === "tester";
  const ok = (() => {
    if (Array.isArray(anyOf) && anyOf.length > 0) {
      return isTester ? testerHasAnyPermission(anyOf) : adminHasAnyPermission(anyOf);
    }
    if (Array.isArray(allOf) && allOf.length > 0) {
      return isTester ? testerHasAllPermissions(allOf) : adminHasAllPermissions(allOf);
    }
    return isTester ? testerHasPermission(permission) : adminHasPermission(permission);
  })();
  const toastShown = useRef(false);
  useEffect(() => {
    if (!ok && !toastShown.current) {
      toast.error("You do not have permission to access this page.");
      toastShown.current = true;
    }
  }, [ok]);
  if (!ok) {
    return <Navigate to="/admin/course-list" replace />;
  }
  return children;
};

// Tutor Protected Route – redirect to tutor login (not main login)
const RequireTutor = ({ children }) => {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  return token && (role === "tutor" || role === "tester")
    ? children
    : <SafeRedirect to="/tutor-login" />;
};

// Role-Based Route (student, tutor, parent) — testers may access any portal
const RequireRole = ({ children, allowedRoles }) => {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");
  if (!token) return <SafeRedirect to="/login" />;
  return (allowedRoles.includes(role) || role === "tester")
    ? children
    : <SafeRedirect to="/login" />;
};

// Guest Route - blocks access if already logged in
const GuestRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  if (token) {
    if (role === "tester" || isAdminPortalRole(role)) {
      return <SafeRedirect to="/admin/course-list" />;
    }
    switch (normalizePortalRole(role)) {
      case "student":
        return <SafeRedirect to="/student" />;
      case "parent":
        return <SafeRedirect to="/parent/" />;
      case "tutor":
        return <SafeRedirect to="/tutor" />;
      default:
        // Unknown/stale role: clear and show login to avoid redirect loop with "/"
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        localStorage.removeItem("userData");
        return children;
    }
  }

  return children;
};


// Redirect users to their main dashboard
// On admin.11plusmagic.co.uk (REACT_APP_ADMIN_HOSTNAME) unauthenticated users go to /admin-login
// On online.11plusmagic.co.uk unauthenticated users go to /login
const PARENT_PREFIX = process.env.REACT_APP_PARENT_PREFIX || "parent";
const STUDENT_PREFIX = process.env.REACT_APP_STUDENT_PREFIX || "student";

/** Student index: dashboard or first allowed section (RBAC). */
const StudentHomeGate = () => {
  const isTester = localStorage.getItem("role") === "tester";
  const checkPerm = isTester ? testerHasPermission : studentHasPermission;
  const checkAny  = isTester ? testerHasAnyPermission : studentHasAnyPermission;
  const { studentPermissionsSynced } = useOutletContext() ?? {
    studentPermissionsSynced: true,
  };
  if (!isTester && !studentPermissionsSynced) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 240,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (checkPerm("student-portal-view-dashboard")) {
    return <StudentDashboard />;
  }
  const fallbacks = [
    { perm: "student-portal-homework-my-assignment", path: "my-current-assignment" },
    { perm: "student-portal-homework-assignment-results", path: "my-course-test" },
    { perm: "student-portal-video-my-course-video", path: "my-course-video" },
    { perm: "student-portal-video-my-video-views", path: "my-course-view" },
    { perm: "student-portal-worksheet", path: "ai-answer-sheet" },
    { perm: "student-portal-mock-exams", path: "mock-exams" },
    // Past papers: own permission, or mock exams (same cohort until DB/seeder assigns past-papers everywhere)
    {
      anyOf: ["student-portal-past-papers", "student-portal-mock-exams"],
      path: "past-papers",
    },
    { perm: "student-portal-flash-cards", path: "flash-cards" },
    { perm: "student-portal-flash-cards-quiz", path: "flash-cards?mode=quiz" },
    { perm: "student-portal-classes", path: "classes" },
    { perm: "student-portal-games-play", path: "games" },
    { perm: "student-portal-games-leaderboard", path: "games/leaderboard" },
    { perm: "student-portal-announcements", path: "announcements" },
    { perm: "student-portal-certificates", path: "certificates" },
    { perm: "student-portal-view-profile", path: "profile" },
  ];
  for (const entry of fallbacks) {
    const ok = entry.anyOf?.length
      ? checkAny(entry.anyOf)
      : checkPerm(entry.perm);
    if (ok) {
      return <Navigate to={`/${STUDENT_PREFIX}/${entry.path}`} replace />;
    }
  }
  return (
    <Box sx={{ p: 3 }}>
      <Typography>
        You do not have access to any student portal sections. Contact your administrator.
      </Typography>
    </Box>
  );
};

/** Tutor index: dashboard or first allowed section (RBAC). */
const TutorHomeGate = () => {
  const isTester = localStorage.getItem("role") === "tester";
  const checkPerm = isTester ? testerHasPermission : tutorHasPermission;
  const { tutorPermissionsSynced } = useOutletContext() ?? {
    tutorPermissionsSynced: true,
  };
  if (!isTester && !tutorPermissionsSynced) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 240,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (checkPerm("tutor-portal-view-dashboard")) {
    return <TutorDashboard />;
  }
  const fallbacks = [
    { perm: "tutor-portal-students", path: "students" },
    { perm: "tutor-portal-courses", path: "courses" },
    { perm: "tutor-portal-classes", path: "classes" },
    { perm: "tutor-portal-mock-exams", path: "mock-exams" },
    { perm: "tutor-portal-papers", path: "papers" },
    { perm: "tutor-portal-timeslots", path: "timeslots" },
    { perm: "tutor-portal-assignments", path: "assignments" },
    { perm: "tutor-portal-tests", path: "tests" },
    { perm: "tutor-portal-announcements", path: "announcements" },
    { perm: "tutor-portal-awards", path: "awards" },
    { perm: "tutor-portal-certificates", path: "certificates" },
  ];
  for (const { perm, path } of fallbacks) {
    if (checkPerm(perm)) {
      return <Navigate to={`/tutor/${path}`} replace />;
    }
  }
  return (
    <Box sx={{ p: 3 }}>
      <Typography>
        You do not have access to any tutor portal sections. Contact your administrator.
      </Typography>
    </Box>
  );
};

/** Parent index: dashboard or first section the user may access (RBAC). */
const ParentHomeGate = () => {
  const isTester = localStorage.getItem("role") === "tester";
  const checkPerm = isTester ? testerHasPermission : parentHasPermission;
  const { parentPermissionsSynced } = useOutletContext() ?? {
    parentPermissionsSynced: true,
  };
  if (!isTester && !parentPermissionsSynced) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 240,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (checkPerm("parent-panel-view-dashboard")) {
    return <ParentDashboard />;
  }
  const fallbacks = [
    { perm: "parent-panel-view-my-students", path: "my-student-list" },
    { perm: "parent-panel-manage-students", path: "my-student-list" },
    { perm: "parent-panel-change-student-password", path: "change-password" },
    { perm: "parent-panel-view-end-of-report", path: "end-of-report" },
    { perm: "parent-panel-view-course-target-area", path: "course-target-area" },
    { perm: "parent-panel-view-finished-test", path: "finished-test" },
    { perm: "parent-panel-view-test-scores", path: "test-scores" },
    { perm: "parent-panel-view-ai-answer-sheet", path: "ai-answer-sheet" },
    { perm: "parent-panel-view-billing", path: "billing/course" },
    { perm: "parent-panel-view-announcements", path: "announcements" },
    { perm: "parent-panel-view-certificates", path: "certificates" },
    { perm: "parent-panel-manage-billing-information", path: "billing-information" },
    { perm: "parent-panel-account-settings", path: "settings" },
  ];
  for (const { perm, path } of fallbacks) {
    if (checkPerm(perm)) {
      return <Navigate to={`/${PARENT_PREFIX}/${path}`} replace />;
    }
  }
  return (
    <Box sx={{ p: 3 }}>
      <Typography>
        You do not have access to any parent portal sections. Contact your administrator.
      </Typography>
    </Box>
  );
};

const RequireParentPermission = ({
  permission,
  anyOf,
  allOf,
  allowBypassWhenMustChangePassword,
  children,
}) => {
  const isTester = localStorage.getItem("role") === "tester";
  const mustChange =
    typeof window !== "undefined" &&
    localStorage.getItem("mustChangePassword") === "1";
  const ok = (() => {
    if (
      allowBypassWhenMustChangePassword &&
      mustChange &&
      permission === "parent-panel-account-settings"
    ) {
      return true;
    }
    if (Array.isArray(anyOf) && anyOf.length > 0) {
      return isTester ? testerHasAnyPermission(anyOf) : parentHasAnyPermission(anyOf);
    }
    if (Array.isArray(allOf) && allOf.length > 0) {
      return isTester ? testerHasAllPermissions(allOf) : parentHasAllPermissions(allOf);
    }
    return isTester ? testerHasPermission(permission) : parentHasPermission(permission);
  })();
  const toastShown = useRef(false);
  useEffect(() => {
    if (!ok && !toastShown.current) {
      toast.error("You do not have permission to access this page.");
      toastShown.current = true;
    }
  }, [ok]);
  if (!ok) {
    return <Navigate to={`/${PARENT_PREFIX}`} replace />;
  }
  return children;
};

const RequireStudentPermission = ({
  permission,
  anyOf,
  allOf,
  children,
}) => {
  const isTester = localStorage.getItem("role") === "tester";
  const ok = (() => {
    if (Array.isArray(anyOf) && anyOf.length > 0) {
      return isTester ? testerHasAnyPermission(anyOf) : studentHasAnyPermission(anyOf);
    }
    if (Array.isArray(allOf) && allOf.length > 0) {
      return isTester ? testerHasAllPermissions(allOf) : studentHasAllPermissions(allOf);
    }
    return isTester ? testerHasPermission(permission) : studentHasPermission(permission);
  })();
  const toastShown = useRef(false);
  useEffect(() => {
    if (!ok && !toastShown.current) {
      toast.error("You do not have permission to access this page.");
      toastShown.current = true;
    }
  }, [ok]);
  if (!ok) {
    return <Navigate to={`/${STUDENT_PREFIX}`} replace />;
  }
  return children;
};

const RequireTutorPermission = ({
  permission,
  anyOf,
  allOf,
  children,
}) => {
  const isTester = localStorage.getItem("role") === "tester";
  const ok = (() => {
    if (Array.isArray(anyOf) && anyOf.length > 0) {
      return isTester ? testerHasAnyPermission(anyOf) : tutorHasAnyPermission(anyOf);
    }
    if (Array.isArray(allOf) && allOf.length > 0) {
      return isTester ? testerHasAllPermissions(allOf) : tutorHasAllPermissions(allOf);
    }
    return isTester ? testerHasPermission(permission) : tutorHasPermission(permission);
  })();
  const toastShown = useRef(false);
  useEffect(() => {
    if (!ok && !toastShown.current) {
      toast.error("You do not have permission to access this page.");
      toastShown.current = true;
    }
  }, [ok]);
  if (!ok) {
    return <Navigate to="/tutor" replace />;
  }
  return children;
};

const RedirectByRole = () => {
  const role = localStorage.getItem("role");
  const token = localStorage.getItem("token");
  const isAdminHost = window.location.hostname === process.env.REACT_APP_ADMIN_HOSTNAME;
  const defaultLogin = isAdminHost ? "/admin-login" : "/login";
  if (!token) return <SafeRedirect to={defaultLogin} />;
  if (role === "tester" || isAdminPortalRole(role)) {
    return <SafeRedirect to="/admin/course-list" />;
  }
  switch (normalizePortalRole(role)) {
    case "student":
      return <SafeRedirect to="/student" />;
    case "parent":
      return <SafeRedirect to="/parent" />;
    case "tutor":
      return <SafeRedirect to="/tutor" />;
    default:
      return <SafeRedirect to={defaultLogin} />;
  }
};

const AppRoutes = () => {
  return (
    <Routes>

      {/* PUBLIC ROUTES */}
      <Route path="/sign-up" element={
        <GuestRoute>
          <SignUp />
        </GuestRoute>
      } />
      <Route path="/parent-sso" element={<ParentSso />} />
      <Route path="/login" element={
        <GuestRoute>
          <Login />
        </GuestRoute>
      } />
      <Route path="/admin-login" element={
        <GuestRoute>
          <AdminLogin />
        </GuestRoute>
      } />
      <Route path="/tutor-login" element={
        <GuestRoute>
          <TutorLogin />
        </GuestRoute>
      } />
      <Route path="/forgot-password" element={
        <GuestRoute>
          <ForgotPassword />
        </GuestRoute>
      } />
      <Route path="/reset-password" element={
        <GuestRoute>
          <ResetPassword />
        </GuestRoute>
      } />

      {/* ADMIN ROUTES */}
      <Route path="/admin" element={
        <RequireAdmin>
          <AdminLayout />
        </RequireAdmin>
      }>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="resource-allocation" element={<ResourceAllocation />} />
        <Route path="holiday-management" element={<HolidayManagement />} />
        <Route
          path="analytics"
          element={
            <RequireAdminPermission permission="view-analytics">
              <AnalyticsLayout />
            </RequireAdminPermission>
          }
        >
          <Route index element={<Navigate to="batch-capacity" replace />} />
          <Route path="batch-capacity" element={<BatchCapacityAnalytics />} />
          <Route path="payment-overdue" element={<PaymentOverdueAnalytics />} />
          <Route path="academic-support" element={<AcademicSupportAnalytics />} />
          <Route path="target-schools" element={<TargetSchoolsAnalytics />} />
          <Route path="revision-planning" element={<RevisionPlanningAnalytics />} />
          <Route path="geographic" element={<GeographicAnalytics />} />
          <Route path="app-usage" element={<AppUsageAnalytics />} />
          <Route path="notifications" element={<NotificationsAnalytics />} />
        </Route>
        {/* Create vs edit: sidebar already splits permissions; routes must match so deep links respect RBAC */}
        <Route
          path="course/:id/edit"
          element={
            <RequireAdminPermission permission="update-courses">
              <Course />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course/:id"
          element={
            <RequireAdminPermission permission="view-courses">
              <CourseView />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course"
          element={
            <RequireAdminPermission permission="create-courses">
              <Course />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course-assignment/:id"
          element={
            <RequireAdminPermission anyOf={["update-course-durations", "update-assignments"]}>
              <CourseAssignment />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course-assignment"
          element={
            <RequireAdminPermission anyOf={["create-course-durations", "create-assignments"]}>
              <CourseAssignment />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course-content/:type?/:id?"
          element={
            <RequireAdminPermission anyOf={["view-course-topics", "create-course-topics", "update-course-topics", "delete-course-topics"]}>
              <CourseContent />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course-test/:id"
          element={
            <RequireAdminPermission permission="update-assignments">
              <CourseTest />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course-test"
          element={
            <RequireAdminPermission anyOf={["create-assignments", "use-ai-assignment-creator"]}>
              <CourseTest />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course-list"
          element={
            <RequireAdminPermission permission="view-courses">
              <CourseList />
            </RequireAdminPermission>
          }
        />
        <Route
          path="timeslot"
          element={
            <RequireAdminPermission anyOf={["view-course-timeslots", "create-course-timeslots", "update-course-timeslots", "delete-course-timeslots"]}>
              <TimeSlots />
            </RequireAdminPermission>
          }
        />
        <Route
          path="assignment-list"
          element={
            <RequireAdminPermission anyOf={["view-course-durations", "view-assignments", "create-assignments", "update-assignments", "delete-assignments"]}>
              <CourseAssignmentList />
            </RequireAdminPermission>
          }
        />
        <Route
          path="topic/subtopic-list"
          element={
            <RequireAdminPermission anyOf={["view-course-topics", "create-course-topics", "update-course-topics", "delete-course-topics"]}>
              <TopicSubtopicList />
            </RequireAdminPermission>
          }
        />
        <Route path="test-list" element={
          <RequireAdminPermission anyOf={["view-assignments", "create-assignments", "update-assignments", "delete-assignments", "use-ai-assignment-creator"]}>
            <TestList />
          </RequireAdminPermission>
        } />
        <Route
          path="course-report"
          element={
            <RequireAdminPermission permission="view-course-reports">
              <CourseReport />
            </RequireAdminPermission>
          }
        />
        <Route
          path="exam-modes"
          element={
            <RequireAdminPermission permission="manage-exam-modes">
              <ExamModeManagement />
            </RequireAdminPermission>
          }
        />
        <Route
          path="settings"
          element={
            <RequireAdminPermission permission="manage-exam-modes">
              <AdminSettings />
            </RequireAdminPermission>
          }
        />
        <Route path="mock-exam-categories" element={
          <RequireAdminPermission anyOf={["view-mock-exam-categories", "create-mock-exam-categories", "update-mock-exam-categories", "delete-mock-exam-categories"]}>
            <MockExamCategory />
          </RequireAdminPermission>
        } />
        <Route path="paper-categories" element={
          <RequireAdminPermission anyOf={["view-paper-categories", "create-paper-categories", "update-paper-categories", "delete-paper-categories"]}>
            <PaperCategory />
          </RequireAdminPermission>
        } />
        <Route
          path="mock-exams"
          element={
            <RequireAdminPermission
              anyOf={[
                "view-mock-exams",
                "create-mock-exams",
                "update-mock-exams",
                "delete-mock-exams",
              ]}
            >
              <MockExam />
            </RequireAdminPermission>
          }
        />
        <Route path="mock-exams/billing" element={
          <RequireAdminPermission permission="view-parents-subscriptions">
            <ParentList forceBillingView billingType="mock" />
          </RequireAdminPermission>
        } />
        <Route path="mock-exams/paper-extract" element={
          <RequireAdminPermission anyOf={["view-paper-extracts", "create-paper-extracts", "update-paper-extracts", "delete-paper-extracts"]}>
            <PaperExtract />
          </RequireAdminPermission>
        } />
        {/* <Route path="paper-categories" element={<PaperCategory />} /> */}
        <Route path="papers" element={
          <RequireAdminPermission anyOf={["view-papers", "create-papers", "update-papers", "delete-papers"]}>
            <Paper />
          </RequireAdminPermission>
        } />
        <Route path="papers/billing" element={
          <RequireAdminPermission permission="view-paper-purchases">
            <AdminPaperPurchases />
          </RequireAdminPermission>
        } />
        <Route path="billing-transactions" element={
          <RequireAdminPermission anyOf={["view-parents-subscriptions", "view-mobile-users"]}>
            <AdminBillingTransactions />
          </RequireAdminPermission>
        } />
        <Route path="mobile-users" element={
          <RequireAdminPermission anyOf={["view-mobile-users", "view-parents-subscriptions"]}>
            <MobileUsers />
          </RequireAdminPermission>
        } />
        <Route path="mobile-users/:id" element={
          <RequireAdminPermission anyOf={["view-mobile-users", "view-parents-subscriptions"]}>
            <MobileUserDetails />
          </RequireAdminPermission>
        } />
        <Route path="master-forms" element={
          <RequireAdminPermission anyOf={masterFormRoutePermissions()}>
            <MasterForm />
          </RequireAdminPermission>
        } />

        {/* Admin Student Management Routes */}
        <Route path="students" element={
          <RequireAdminPermission permission="view-students">
            <StudentList />
          </RequireAdminPermission>
        } />
        <Route path="student/add" element={
          <RequireAdminPermission permission="create-students">
            <StudentForm />
          </RequireAdminPermission>
        } />
        <Route path="student/:id/edit" element={
          <RequireAdminPermission permission="update-students">
            <StudentForm />
          </RequireAdminPermission>
        } />
        <Route path="student/:id" element={
          <RequireAdminPermission permission="view-students">
            <StudentDetails />
          </RequireAdminPermission>
        } />

        {/* Admin Tutor Management Routes */}
        <Route path="tutors" element={
          <RequireAdminPermission permission="view-tutors">
            <TutorList />
          </RequireAdminPermission>
        } />
        <Route path="tutor/add" element={
          <RequireAdminPermission permission="update-tutors">
            <TutorForm />
          </RequireAdminPermission>
        } />
        <Route path="tutor/edit/:id" element={
          <RequireAdminPermission permission="update-tutors">
            <TutorForm />
          </RequireAdminPermission>
        } />

        {/* Admin Parent Management Routes */}
        <Route path="parents" element={
          <RequireAdminPermission permission="view-parents">
            <ParentList />
          </RequireAdminPermission>
        } />
        <Route path="billing" element={
          <RequireAdminPermission permission="view-parents-subscriptions">
            <ParentList />
          </RequireAdminPermission>
        } />
        <Route path="course/billing" element={
          <RequireAdminPermission permission="view-parents-subscriptions">
            <ParentList />
          </RequireAdminPermission>
        } />
        <Route path="parent/:parentId/billing" element={
          <RequireAdminPermission permission="view-parents-subscriptions">
            <ParentBilling />
          </RequireAdminPermission>
        } />

        {/* AI Assignment Creator */}
        <Route path="ai-assignment-creator" element={
          <RequireAdminPermission anyOf={["use-ai-assignment-creator", "create-assignments", "update-assignments"]}>
            <AiAssignmentCreator />
          </RequireAdminPermission>
        } />


        {/* Question Bank */}
        <Route path="question-bank" element={
          <RequireAdminPermission permission="view-question-bank">
            <QuestionBankList />
          </RequireAdminPermission>
        } />
        <Route path="flagged-questions" element={
          <RequireAdminPermission permission="view-flagged-questions">
            <FlaggedQuestions />
          </RequireAdminPermission>
        } />
        <Route path="verification-dashboard" element={
          <RequireAdminPermission permission="view-verification-dashboard">
            <VerificationDashboard />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/papers" element={
          <RequireAdminPermission permission="view-question-bank-papers">
            <QuestionBankPapers />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/papers/:id" element={
          <RequireAdminPermission permission="view-question-bank-papers">
            <QuestionBankPaperViewer />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/recent-imports" element={
          <RequireAdminPermission permission="view-question-bank-imports">
            <QuestionBankRecentImports />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/create" element={
          <RequireAdminPermission permission="create-question-bank">
            <QuestionBankForm />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/:id/edit" element={
          <RequireAdminPermission permission="update-question-bank">
            <QuestionBankForm />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/import" element={
          <RequireAdminPermission anyOf={["view-question-bank-imports", "create-question-bank-imports"]}>
            <QuestionBankImport />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/import/:id/review" element={
          <RequireAdminPermission permission="view-question-bank-imports">
            <QuestionBankImportReview />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/similar-papers" element={
          <RequireAdminPermission anyOf={["view-question-bank-similar-papers", "update-question-bank-similar-papers"]}>
            <SimilarPaperReview />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/mcq-review" element={
          <RequireAdminPermission permission="view-question-bank-imports">
            <McqReview />
          </RequireAdminPermission>
        } />

        {/* Admin Announcements Routes */}
        <Route path="announcements" element={
          <RequireAdminPermission permission="view-announcements">
            <Announcements />
          </RequireAdminPermission>
        } />

        {/* Admin Flash Cards Routes */}
        <Route path="flash-cards" element={
          <RequireAdminPermission permission="view-flash-cards">
            <FlashCardList />
          </RequireAdminPermission>
        } />
        <Route path="flash-cards/add" element={
          <RequireAdminPermission permission="create-flash-cards">
            <FlashCardForm />
          </RequireAdminPermission>
        } />
        <Route path="flash-cards/:id/edit" element={
          <RequireAdminPermission permission="update-flash-cards">
            <FlashCardForm />
          </RequireAdminPermission>
        } />

        {/* Admin Games — Game topics */}
        <Route path="game-topics" element={
          <RequireAdminPermission permission="view-game-topics">
            <GameTopicList />
          </RequireAdminPermission>
        } />
        <Route path="game-topics/add" element={
          <RequireAdminPermission permission="create-game-topics">
            <GameTopicForm />
          </RequireAdminPermission>
        } />
        <Route path="game-topics/:id/edit" element={
          <RequireAdminPermission permission="update-game-topics">
            <GameTopicForm />
          </RequireAdminPermission>
        } />
        <Route path="game-topics/:id" element={
          <RequireAdminPermission anyOf={["view-game-topics", "update-game-topics"]}>
            <GameTopicView />
          </RequireAdminPermission>
        } />

        {/* Admin Games — Game contents */}
        <Route path="game-contents" element={
          <RequireAdminPermission permission="view-game-contents">
            <GameContentList />
          </RequireAdminPermission>
        } />
        <Route path="game-contents/add" element={
          <RequireAdminPermission permission="create-game-contents">
            <GameContentForm />
          </RequireAdminPermission>
        } />
        <Route path="game-contents/:id/edit" element={
          <RequireAdminPermission permission="update-game-contents">
            <GameContentForm />
          </RequireAdminPermission>
        } />
        <Route path="game-contents/:id" element={
          <RequireAdminPermission anyOf={["view-game-contents", "update-game-contents"]}>
            <GameContentView />
          </RequireAdminPermission>
        } />

        {/* Admin Awards Routes */}
        <Route path="awards" element={
          <RequireAdminPermission permission="view-awards">
            <Awards />
          </RequireAdminPermission>
        } />

        {/* Admin Certificates Routes */}
        <Route path="certificates" element={
          <RequireAdminPermission permission="view-certificates">
            <Certificates />
          </RequireAdminPermission>
        } />

        {/* Admin Leads Routes */}
        <Route path="leads" element={
          <RequireAdminPermission permission="view-leads">
            <AdminLeads />
          </RequireAdminPermission>
        } />
        <Route path="tuition-requests" element={
          <RequireAdminPermission permission="view-leads">
            <TuitionRequests />
          </RequireAdminPermission>
        } />
        <Route path="mobile-revision-support-requests" element={
          <RequireAdminPermission permission="view-leads">
            <MobileRevisionSupportRequests />
          </RequireAdminPermission>
        } />
        <Route path="mobile-help-support" element={
          <RequireAdminPermission anyOf={["view-leads", "view-support-chat"]}>
            <MobileHelpSupport />
          </RequireAdminPermission>
        } />
        <Route path="mobile-app-issues" element={
          <RequireAdminPermission anyOf={["view-leads", "view-support-chat"]}>
            <MobileAppIssues />
          </RequireAdminPermission>
        } />
        <Route path="whatsapp" element={
          <RequireAdminPermission permission="send-whatsapp-msg">
            <WhatsAppCampaign />
          </RequireAdminPermission>
        } />

        {/* Support Chat (admin inbox) */}
        <Route
          path="support-chat"
          element={
            <RequireAdminPermission permission="view-support-chat">
              <SupportChatInbox />
            </RequireAdminPermission>
          }
        />

        {/* Audit Trail (user activity log) */}
        <Route
          path="audit-trail"
          element={
            <RequireAdminPermission permission="view-audit-trail">
              <AuditTrailList />
            </RequireAdminPermission>
          }
        />

        {/* Email Subscriptions */}
        <Route
          path="subscriptions"
          element={
            <RequireAdminPermission permission="view-leads">
              <EmailSubscriptions />
            </RequireAdminPermission>
          }
        />
      </Route>

      {/* STUDENT ROUTES */}
      <Route path="student" element={
        <RequireRole allowedRoles={["student"]}>
          <StudentLayout />
        </RequireRole>
      }>
        <Route index element={<StudentHomeGate />} />
        <Route path="classes" element={
          <RequireStudentPermission permission="student-portal-classes">
            <StudentClasses />
          </RequireStudentPermission>
        } />
        <Route path="announcements" element={
          <RequireStudentPermission permission="student-portal-announcements">
            <StudentAnnouncements />
          </RequireStudentPermission>
        } />
        <Route path="certificates" element={
          <RequireStudentPermission permission="student-portal-certificates">
            <StudentCertificates />
          </RequireStudentPermission>
        } />
        <Route path="my-course-test" element={
          <RequireStudentPermission permission="student-portal-homework-assignment-results">
            <MyCourseTest />
          </RequireStudentPermission>
        } />
        <Route path="course-target-area" element={
          <RequireStudentPermission permission="student-portal-homework-assignment-results">
            <MyStudentList />
          </RequireStudentPermission>
        } />
        <Route path="test-scores" element={
          <RequireStudentPermission permission="student-portal-homework-assignment-results">
            <TestScores />
          </RequireStudentPermission>
        } />
        <Route path="finished-test" element={
          <RequireStudentPermission permission="student-portal-homework-assignment-results">
            <FinishedTest />
          </RequireStudentPermission>
        } />

        <Route path="videos" element={
          <RequireStudentPermission
            anyOf={["student-portal-video-my-course-video", "student-portal-video-my-video-views"]}
          >
            <VideoLessonsPage />
          </RequireStudentPermission>
        } />
        <Route path="topic/content/view/:topic_id" element={
          <RequireStudentPermission permission="student-portal-video-my-course-video">
            <TopicContentView />
          </RequireStudentPermission>
        } />
        <Route path="topic/test/:test_id" element={
          <RequireStudentPermission permission="student-portal-video-my-course-video">
            <TopicTest />
          </RequireStudentPermission>
        } />
        <Route path="subtopic/content/view/:sub_topic_id" element={
          <RequireStudentPermission permission="student-portal-video-my-course-video">
            <SubTopicContentView />
          </RequireStudentPermission>
        } />
        <Route path="subtopic/test/:sub_topic_test_id" element={
          <RequireStudentPermission permission="student-portal-video-my-course-video">
            <SubTopicTest />
          </RequireStudentPermission>
        } />
        <Route path="my-course-video-page" element={
          <RequireStudentPermission permission="student-portal-video-my-course-video">
            <MyCourseVideoPage />
          </RequireStudentPermission>
        } />
        <Route path="ai-answer-sheet" element={
          <RequireStudentPermission anyOf={["student-portal-worksheet", "verify-worksheet"]}>
            <AiAnswerSheetUpload />
          </RequireStudentPermission>
        } />
        <Route path="work-sheet/:id/take" element={
          <RequireStudentPermission anyOf={["student-portal-worksheet", "verify-worksheet"]}>
            <WorkSheetTake />
          </RequireStudentPermission>
        } />
        <Route path="work-sheet/:testId/attempt/:attemptId/review" element={<WorkSheetReview />} />
        <Route path="work-sheet/:testId/sweep-history" element={<WorkSheetSweepHistory />} />

        <Route path="add-student" element={
          <RequireStudentPermission permission="student-portal-view-dashboard">
            <AddStudent />
          </RequireStudentPermission>
        } />
        <Route path="my-current-assignment" element={
          <RequireStudentPermission permission="student-portal-homework-my-assignment">
            <MyCurrentCourseAssignment />
          </RequireStudentPermission>
        } />
        <Route path="test" element={
          <RequireStudentPermission permission="student-portal-homework-my-assignment">
            <StudentTest />
          </RequireStudentPermission>
        } />
        <Route path="my-course-video" element={
          <RequireStudentPermission permission="student-portal-video-my-course-video">
            <MyCourseVideo />
          </RequireStudentPermission>
        } />
        <Route path="my-course-view" element={
          <RequireStudentPermission permission="student-portal-video-my-video-views">
            <MyCourseView />
          </RequireStudentPermission>
        } />

        <Route path="mock-exams" element={
          <RequireStudentPermission permission="student-portal-mock-exams">
            <StudentMockExams />
          </RequireStudentPermission>
        } />
        <Route path="mock-exam/exam/:mockExamId/report" element={
          <RequireStudentPermission permission="student-portal-mock-exams">
            <StudentMockExamExamReport />
          </RequireStudentPermission>
        } />
        <Route path="mock-exam/result/:attemptId/report" element={
          <RequireStudentPermission permission="student-portal-mock-exams">
            <StudentMockExamReport />
          </RequireStudentPermission>
        } />
        <Route path="mock-exam/result/:attemptId/review" element={
          <RequireStudentPermission permission="student-portal-mock-exams">
            <StudentMockExamReview />
          </RequireStudentPermission>
        } />
        <Route path="mock-exam/:id/take" element={
          <RequireStudentPermission permission="student-portal-mock-exams">
            <StudentMockExamTake />
          </RequireStudentPermission>
        } />
        <Route path="past-papers" element={
          <RequireStudentPermission
            anyOf={["student-portal-past-papers", "student-portal-mock-exams", "verify-paper"]}
          >
            <StudentPastPapers />
          </RequireStudentPermission>
        } />
        <Route path="past-paper/:purchaseId/take" element={
          <RequireStudentPermission
            anyOf={["student-portal-past-papers", "student-portal-mock-exams", "verify-paper"]}
          >
            <StudentPaperTake />
          </RequireStudentPermission>
        } />
        <Route path="past-paper/:purchaseId/result" element={
          <RequireStudentPermission
            anyOf={["student-portal-past-papers", "student-portal-mock-exams", "verify-paper"]}
          >
            <StudentPaperResult />
          </RequireStudentPermission>
        } />
        <Route path="past-paper/:purchaseId/review" element={
          <RequireStudentPermission
            anyOf={["student-portal-past-papers", "student-portal-mock-exams", "verify-paper"]}
          >
            <StudentPaperReview />
          </RequireStudentPermission>
        } />
        <Route path="past-paper-report/:paperId" element={
          <RequireStudentPermission
            anyOf={["student-portal-past-papers", "student-portal-mock-exams", "verify-paper"]}
          >
            <StudentPaperReport />
          </RequireStudentPermission>
        } />
        <Route path="games" element={
          <RequireStudentPermission permission="student-portal-games-play">
            <Games />
          </RequireStudentPermission>
        } />
        <Route path="games/leaderboard" element={
          <RequireStudentPermission permission="student-portal-games-leaderboard">
            <GameLeaderboard />
          </RequireStudentPermission>
        } />
        <Route path="games/:subjectKey" element={
          <RequireStudentPermission permission="student-portal-games-play">
            <Games />
          </RequireStudentPermission>
        } />
        <Route path="flash-cards" element={
          <RequireStudentPermission
            anyOf={["student-portal-flash-cards", "student-portal-flash-cards-quiz"]}
          >
            <FlashCards />
          </RequireStudentPermission>
        } />
        <Route path="flash-cards/:subjectKey" element={
          <RequireStudentPermission
            anyOf={["student-portal-flash-cards", "student-portal-flash-cards-quiz"]}
          >
            <FlashCards />
          </RequireStudentPermission>
        } />
        <Route path="vocabulary/:subjectKey" element={
          <RequireStudentPermission
            anyOf={["student-portal-flash-cards", "student-portal-flash-cards-quiz"]}
          >
            <FlashCards />
          </RequireStudentPermission>
        } />
        <Route path="profile" element={
          <RequireStudentPermission permission="student-portal-view-profile">
            <StudentProfile />
          </RequireStudentPermission>
        } />
      </Route>

      {/* PARENT ROUTES */}
      <Route path="/parent" element={
        <RequireRole allowedRoles={["parent"]}>
          <ParentLayout />
        </RequireRole>
      }>
        <Route index element={<ParentHomeGate />} />
        <Route path="readiness" element={
          <RequireParentPermission
            anyOf={["parent-panel-view-dashboard", "parent-panel-view-end-of-report"]}
          >
            <ParentReadinessOverview />
          </RequireParentPermission>
        } />
        <Route path="readiness/:studentId/:targetSchoolId" element={
          <RequireParentPermission
            anyOf={["parent-panel-view-dashboard", "parent-panel-view-end-of-report"]}
          >
            <SchoolReadinessDetail />
          </RequireParentPermission>
        } />
        <Route path="announcements" element={
          <RequireParentPermission permission="parent-panel-view-announcements">
            <ParentAnnouncements />
          </RequireParentPermission>
        } />
        <Route path="certificates" element={
          <RequireParentPermission permission="parent-panel-view-certificates">
            <ParentCertificates />
          </RequireParentPermission>
        } />
        <Route path="add-student" element={
          <RequireParentPermission permission="parent-panel-manage-students">
            <AddStudent />
          </RequireParentPermission>
        } />
        <Route path="edit-student/:studentId" element={
          <RequireParentPermission permission="parent-panel-manage-students">
            <EditStudent />
          </RequireParentPermission>
        } />
        <Route path="my-student-list" element={
          <RequireParentPermission
            anyOf={["parent-panel-view-my-students", "parent-panel-manage-students"]}
          >
            <MyStudentList />
          </RequireParentPermission>
        } />
        <Route path="change-password" element={
          <RequireParentPermission permission="parent-panel-change-student-password">
            <ChangePassword />
          </RequireParentPermission>
        } />

        <Route path="billing" element={<SafeRedirect to={`/${process.env.REACT_APP_PARENT_PREFIX || 'parent'}/billing/course`} />} />
        <Route path="billing/course" element={
          <RequireParentPermission permission="parent-panel-view-billing">
            <Billing />
          </RequireParentPermission>
        } />
        <Route path="billing/mock" element={
          <RequireParentPermission permission="parent-panel-view-billing">
            <Billing />
          </RequireParentPermission>
        } />
        <Route path="billing/paper" element={
          <RequireParentPermission permission="parent-panel-view-billing">
            <Billing />
          </RequireParentPermission>
        } />
        <Route path="billing-information" element={
          <RequireParentPermission permission="parent-panel-manage-billing-information">
            <BillingInformation />
          </RequireParentPermission>
        } />

        <Route path="end-of-report" element={
          <RequireParentPermission permission="parent-panel-view-end-of-report">
            <EndOfReport />
          </RequireParentPermission>
        } />
        <Route path="test-scores" element={
          <RequireParentPermission permission="parent-panel-view-test-scores">
            <TestScores />
          </RequireParentPermission>
        } />
        <Route path="finished-test" element={
          <RequireParentPermission permission="parent-panel-view-finished-test">
            <FinishedTest />
          </RequireParentPermission>
        } />
        <Route path="course-target-area" element={
          <RequireParentPermission permission="parent-panel-view-course-target-area">
            <CourseTargetArea />
          </RequireParentPermission>
        } />
        <Route path="ai-answer-sheet" element={
          <RequireParentPermission permission="parent-panel-view-ai-answer-sheet">
            <AiAnswerSheetUpload />
          </RequireParentPermission>
        } />
        <Route path="work-sheet/:id/take" element={
          <RequireParentPermission permission="parent-panel-view-ai-answer-sheet">
            <WorkSheetTake />
          </RequireParentPermission>
        } />
        <Route path="work-sheet/:testId/attempt/:attemptId/review" element={<WorkSheetReview />} />
        <Route path="work-sheet/:testId/sweep-history" element={<WorkSheetSweepHistory />} />

        <Route path="mock-exam-results" element={<ParentMockExamResults />} />
        <Route path="past-paper-results" element={<ParentPastPaperResults />} />
        <Route path="mock-exam/result/:attemptId/report" element={<StudentMockExamReport />} />
        <Route path="mock-exam/result/:attemptId/review" element={<StudentMockExamReview />} />
        <Route path="past-paper/:purchaseId/result" element={<StudentPaperResult />} />
        <Route path="past-paper/:purchaseId/review" element={<StudentPaperReview />} />

        <Route path="settings" element={
          <RequireParentPermission
            permission="parent-panel-account-settings"
            allowBypassWhenMustChangePassword
          >
            <Setting />
          </RequireParentPermission>
        } />
      </Route>

      {/* TUTOR ROUTES (tutor panel; tutors use /tutor-login only) */}
      <Route path="/tutor" element={<RequireTutor><Outlet /></RequireTutor>}>
        {/* Meeting only - no sidebar/app bar; opens in new tab */}
        <Route path="meeting/:roomCode" element={
          <RequireTutorPermission permission="tutor-portal-classes">
            <TutorMeetingPage />
          </RequireTutorPermission>
        } />
        {/* All other tutor pages use TutorLayout */}
        <Route element={<TutorLayout />}>
          <Route index element={<TutorHomeGate />} />
          <Route path="students" element={
            <RequireTutorPermission permission="tutor-portal-students">
              <TutorStudents />
            </RequireTutorPermission>
          } />
          <Route path="courses" element={
            <RequireTutorPermission permission="tutor-portal-courses">
              <TutorCourses />
            </RequireTutorPermission>
          } />
          <Route path="classes" element={
            <RequireTutorPermission permission="tutor-portal-classes">
              <TutorClasses />
            </RequireTutorPermission>
          } />
          <Route path="course/:courseId/class" element={
            <RequireTutorPermission permission="tutor-portal-classes">
              <TutorStartClass />
            </RequireTutorPermission>
          } />
          <Route path="mock-exams" element={
            <RequireTutorPermission permission="tutor-portal-mock-exams">
              <TutorMockExams />
            </RequireTutorPermission>
          } />
          <Route path="papers" element={
            <RequireTutorPermission permission="tutor-portal-papers">
              <TutorPapers />
            </RequireTutorPermission>
          } />
          <Route path="timeslots" element={
            <RequireTutorPermission permission="tutor-portal-timeslots">
              <TutorTimeslots />
            </RequireTutorPermission>
          } />
          <Route path="assignments" element={
            <RequireTutorPermission permission="tutor-portal-assignments">
              <TutorAssignments />
            </RequireTutorPermission>
          } />
          <Route path="tests" element={
            <RequireTutorPermission permission="tutor-portal-tests">
              <TutorTests />
            </RequireTutorPermission>
          } />
          <Route path="notifications" element={
            <RequireTutorPermission permission="tutor-portal-view-dashboard">
              <TutorNotifications />
            </RequireTutorPermission>
          } />
          <Route path="announcements" element={
            <RequireTutorPermission permission="tutor-portal-announcements">
              <TutorAnnouncements />
            </RequireTutorPermission>
          } />
          <Route path="awards" element={
            <RequireTutorPermission permission="tutor-portal-awards">
              <TutorAwards />
            </RequireTutorPermission>
          } />
          <Route path="certificates" element={
            <RequireTutorPermission permission="tutor-portal-certificates">
              <TutorCertificates />
            </RequireTutorPermission>
          } />
        </Route>
      </Route>

      {/* ROOT ROUTE - REDIRECT BY ROLE */}
      <Route path="/" element={<RedirectByRole />} />

      {/* GENERAL AUTH ROUTES (DEFAULT LAYOUT) - FOR BACKWARD COMPATIBILITY */}
      <Route path="/general" element={
        <RequireAuth>
          <Layout />
        </RequireAuth>
      }>
        <Route index element={<Dashboard />} />
        <Route path="student-pannel" element={<StudentPannel />} />
        <Route path="videos" element={<VideoLessonsPage />} />
        <Route path="my-course-video-page" element={<MyCourseVideoPage />} />
      </Route>

      {/* FALLBACK - 404 Not Found */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
